/**
 * GDPR-compliant consent management system for client application
 *
 * This system ensures compliance with GDPR and other privacy regulations
 * by managing user consent for cookies and data processing activities.
 */

import { useState, useEffect, useCallback, useMemo } from "react";

import { logger } from "./logger";
import { sharedApi, getAuthToken } from "../services/sharedApi";

// Consent categories
export interface ConsentPreferences {
  necessary: boolean; // Always true - required for app functionality
  functional: boolean; // App features, user preferences, authentication
  analytics: boolean; // Usage analytics, performance monitoring
  marketing: boolean; // Facebook Pixel, advertising, remarketing
}

// Consent status with metadata
export interface ConsentStatus extends ConsentPreferences {
  timestamp: number;
  version: string; // Consent version for tracking policy changes
  source: "banner" | "settings" | "registration" | "default";
}

// EU country codes for GDPR compliance (for future API-based detection)
// const EU_COUNTRIES = [
//   'AT', 'BE', 'BG', 'CY', 'CZ', 'DE', 'DK', 'EE', 'ES', 'FI', 'FR', 'GR',
//   'HR', 'HU', 'IE', 'IT', 'LT', 'LU', 'LV', 'MT', 'NL', 'PL', 'PT', 'RO',
//   'SE', 'SI', 'SK', 'GB', // Include UK for now
//   // EEA countries
//   'IS', 'LI', 'NO'
// ];

// Current consent policy version
const CONSENT_VERSION = "1.0.0";
const STORAGE_KEY = "lingoshade_consent";

/**
 * Default consent preferences - same for all users globally
 * We apply privacy-first approach (GDPR-style) to all users regardless of location
 */
const getDefaultConsent = (): ConsentPreferences => ({
  necessary: true, // Always required
  functional: false, // Requires explicit consent
  analytics: false, // Requires explicit consent
  marketing: false, // Requires explicit consent
});

/**
 * Consent Manager class for handling all consent operations
 * Applies privacy-first approach to all users globally (no geo-detection)
 */
class ConsentManager {
  private listeners: Set<(status: ConsentStatus | null) => void> = new Set();
  private currentConsent: ConsentStatus | null = null;
  private storageAvailable = true;

  constructor() {
    // Проверяем доступность localStorage
    try {
      const test = "__storage_test__";
      localStorage.setItem(test, test);
      localStorage.removeItem(test);
      this.storageAvailable = true;
    } catch {
      logger.warn(
        "[consent] localStorage is not available, using in-memory storage",
      );
      this.storageAvailable = false;
    }

    this.loadConsent();
  }

  /**
   * Load consent from localStorage
   */
  private loadConsent(): void {
    if (!this.storageAvailable) {
      // Если storage недоступен, используем дефолтные значения
      this.resetConsent();
      return;
    }

    try {
      const stored = localStorage.getItem(STORAGE_KEY);
      if (stored) {
        const consent = JSON.parse(stored) as ConsentStatus;

        // Validate consent version
        if (consent.version === CONSENT_VERSION) {
          this.currentConsent = consent;
        } else {
          logger.info("[consent] Version mismatch, resetting consent");
          this.resetConsent();
        }
      }
    } catch (error) {
      logger.error("[consent] Failed to load consent", error);
      this.storageAvailable = false; // Отключаем storage при ошибке
      this.resetConsent();
    }
  }

  /**
   * Save consent to localStorage
   */
  private saveConsent(): void {
    if (!this.storageAvailable || !this.currentConsent) {
      return;
    }

    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(this.currentConsent));
      logger.debug("[consent] Consent saved", this.currentConsent);
    } catch (error) {
      logger.error("[consent] Failed to save consent", error);
      this.storageAvailable = false; // Отключаем storage при ошибке
    }
  }

  /**
   * Reset consent to defaults
   */
  private resetConsent(): void {
    const defaults = getDefaultConsent();
    this.currentConsent = {
      ...defaults,
      timestamp: Date.now(),
      version: CONSENT_VERSION,
      source: "default",
    };
    if (this.storageAvailable) {
      this.saveConsent();
    }
    // НЕ вызываем notifyListeners() здесь, чтобы избежать бесконечного цикла
  }

  /**
   * Notify all listeners of consent changes
   */
  private notifyListeners(): void {
    this.listeners.forEach((listener) => listener(this.currentConsent));
  }

  /**
   * Get current consent status
   */
  public getConsent(): ConsentStatus | null {
    return this.currentConsent;
  }

  /**
   * Set consent preferences
   */
  public setConsent(
    preferences: Partial<ConsentPreferences>,
    source: ConsentStatus["source"] = "settings",
  ): void {
    const current = this.currentConsent || {
      ...getDefaultConsent(),
      timestamp: Date.now(),
      version: CONSENT_VERSION,
      source: "default" as const,
    };

    // Calculate next state without mutating first to detect meaningful changes
    const nextPreferences: ConsentPreferences = {
      necessary: true,
      functional:
        typeof preferences.functional === "boolean"
          ? preferences.functional
          : current.functional,
      analytics:
        typeof preferences.analytics === "boolean"
          ? preferences.analytics
          : current.analytics,
      marketing:
        typeof preferences.marketing === "boolean"
          ? preferences.marketing
          : current.marketing,
    };
    const nextSource = source ?? current.source;

    const changed =
      nextPreferences.functional !== current.functional ||
      nextPreferences.analytics !== current.analytics ||
      nextPreferences.marketing !== current.marketing ||
      nextSource !== current.source ||
      this.currentConsent === null; // first-time init

    if (!changed) {
      // Avoid redundant writes/notifications to prevent render loops
      logger.debug("[consent] No changes detected, skipping update");
      return;
    }

    this.currentConsent = {
      ...current,
      ...nextPreferences,
      necessary: true, // Always true
      timestamp: Date.now(),
      version: CONSENT_VERSION,
      source: nextSource,
    };

    this.saveConsent();
    this.notifyListeners();

    // Report to backend if authenticated
    void this.reportToServerSafely(source);

    logger.info("[consent] Consent updated", {
      preferences: nextPreferences,
      source: nextSource,
    });
  }

  /**
   * Accept all cookies
   */
  public acceptAll(source: ConsentStatus["source"] = "banner"): void {
    this.setConsent(
      {
        necessary: true,
        functional: true,
        analytics: true,
        marketing: true,
      },
      source,
    );
  }

  /**
   * Reject all optional cookies
   */
  public rejectAll(source: ConsentStatus["source"] = "banner"): void {
    this.setConsent(
      {
        necessary: true,
        functional: false,
        analytics: false,
        marketing: false,
      },
      source,
    );
  }

  /**
   * Subscribe to consent changes
   */
  public subscribe(
    listener: (status: ConsentStatus | null) => void,
  ): () => void {
    this.listeners.add(listener);

    // Immediately call listener with current state
    listener(this.currentConsent);

    // Return unsubscribe function
    return () => {
      this.listeners.delete(listener);
    };
  }

  /**
   * Check if consent is required (no consent given yet or user hasn't made a choice)
   */
  public isConsentRequired(): boolean {
    return !this.currentConsent || this.currentConsent.source === "default";
  }

  /**
   * Get consent summary for compliance reporting
   */
  public getConsentSummary(): {
    hasConsent: boolean;
    consentDate: string | null;
    version: string;
    categories: string[];
  } {
    return {
      hasConsent: !!this.currentConsent,
      consentDate: this.currentConsent
        ? new Date(this.currentConsent.timestamp).toISOString()
        : null,
      version: CONSENT_VERSION,
      categories: this.currentConsent
        ? Object.entries(this.currentConsent)
            .filter(
              ([key, value]) =>
                value === true &&
                ["necessary", "functional", "analytics", "marketing"].includes(
                  key,
                ),
            )
            .map(([key]) => key)
        : [],
    };
  }

  /**
   * Report current consent to backend if authenticated
   */
  private async reportToServerSafely(
    _source?: ConsentStatus["source"],
  ): Promise<void> {
    try {
      const token = getAuthToken();
      if (!token || !this.currentConsent) return;

      const consentId = this.getOrCreateDeviceId();
      const { functional, analytics, marketing, timestamp, version } =
        this.currentConsent;
      await sharedApi.privacy.reportConsent(
        {
          consentId,
          preferences: { functional, analytics, marketing },
          timestamp,
          version,
          isEU: undefined,
        },
        token,
      );
      logger.debug("[consent] Reported to backend");
    } catch (error: unknown) {
      logger.warn(
        "[consent] Failed to report to backend (non-critical)",
        error,
      );
    }
  }

  /**
   * Public wrapper to report current consent to backend if authenticated
   */
  public async syncToServerIfAuthenticated(): Promise<void> {
    await this.reportToServerSafely();
  }

  /**
   * Merge consent from backend if it is newer or has different version
   */
  public mergeFromServer(record: {
    preferences: ConsentPreferences | Record<string, boolean>;
    timestamp: number;
    version: string | number;
    source?: string;
  }): void {
    const local = this.currentConsent;
    const serverTs = record.timestamp;
    const serverVersion = String(record.version);
    const localTs = local?.timestamp ?? 0;
    const localVersion = local ? String(local.version) : "";

    const shouldApply =
      !local || serverTs > localTs || serverVersion !== localVersion;
    if (!shouldApply) return;

    const prefs = record.preferences as Record<string, boolean>;
    this.setConsent(
      {
        functional: !!prefs.functional,
        analytics: !!prefs.analytics,
        marketing: !!prefs.marketing,
      },
      (record.source as ConsentStatus["source"]) || "settings",
    );
  }

  /**
   * Generate or return a persisted device identifier for consent correlation
   */
  private getOrCreateDeviceId(): string {
    try {
      const key = "lingoshade_device_id";
      let id = localStorage.getItem(key);
      if (!id) {
        // Use crypto.randomUUID if available, else fallback
        id =
          typeof crypto !== "undefined" && "randomUUID" in crypto
            ? (crypto as unknown as { randomUUID: () => string }).randomUUID()
            : `${Date.now()}-${Math.random().toString(36).slice(2)}`;
        localStorage.setItem(key, id);
      }
      return id;
    } catch {
      return `${Date.now()}-${Math.random().toString(36).slice(2)}`;
    }
  }
}

// Global consent manager instance
const consentManager = new ConsentManager();

/**
 * React hook for consent management
 */
interface UseConsentReturn {
  consent: {
    necessary: boolean;
    functional: boolean;
    analytics: boolean;
    marketing: boolean;
  } | null;
  updateConsent: (
    preferences: Partial<ConsentPreferences>,
    source?: ConsentStatus["source"],
  ) => void;
  acceptAll: (source?: ConsentStatus["source"]) => void;
  rejectAll: (source?: ConsentStatus["source"]) => void;
  isConsentRequired: () => boolean;
  consentMetadata: {
    timestamp: number;
    version: string;
    source: ConsentStatus["source"];
  } | null;
  summary: ReturnType<ConsentManager["getConsentSummary"]>;
}

export const useConsent = (): UseConsentReturn => {
  const [consent, setConsent] = useState<ConsentStatus | null>(
    consentManager.getConsent(),
  );

  useEffect(() => {
    return consentManager.subscribe(setConsent);
  }, []);

  const updateConsent = useCallback(
    (
      preferences: Partial<ConsentPreferences>,
      source?: ConsentStatus["source"],
    ): void => {
      consentManager.setConsent(preferences, source);
    },
    [],
  );

  const acceptAll = useCallback((source?: ConsentStatus["source"]): void => {
    consentManager.acceptAll(source);
  }, []);

  const rejectAll = useCallback((source?: ConsentStatus["source"]): void => {
    consentManager.rejectAll(source);
  }, []);

  const isConsentRequired = useCallback((): boolean => {
    return consentManager.isConsentRequired();
  }, []);

  // FIXED: Мемоизируем объекты чтобы не создавать новые references при каждом рендере!
  const consentPreferences = useMemo(
    () =>
      consent
        ? {
            necessary: consent.necessary,
            functional: consent.functional,
            analytics: consent.analytics,
            marketing: consent.marketing,
          }
        : null,
    // eslint-disable-next-line react-hooks/exhaustive-deps -- consent object intentionally omitted, using primitives
    [
      consent?.necessary,
      consent?.functional,
      consent?.analytics,
      consent?.marketing,
    ],
  );

  const metadata = useMemo(
    () =>
      consent
        ? {
            timestamp: consent.timestamp,
            version: consent.version,
            source: consent.source,
          }
        : null,
    // eslint-disable-next-line react-hooks/exhaustive-deps -- consent object intentionally omitted, using primitives
    [consent?.timestamp, consent?.version, consent?.source],
  );

  // Summary is managed by singleton, no dependencies needed
  const summary = useMemo(() => consentManager.getConsentSummary(), []);

  return {
    consent: consentPreferences,
    updateConsent,
    acceptAll,
    rejectAll,
    isConsentRequired,
    consentMetadata: metadata,
    summary,
  };
};

// Export consent manager for direct access
export { consentManager };
export default consentManager;
